import i18next from 'i18next'
import type {TranslationData, TypeValue} from '@/types/types'
import type {AppConfig} from '@/hooks/app-config-helpers'
import {createMarkerRegex} from './marker-grammar'

/**
 * Type-table fields on {@link AppConfig} whose values get registered as
 * i18next nested resources. Kept in lockstep with `TYPE_VALUE_KEYS` in
 * `use-app-config.ts` — adding a new top-level type table needs both.
 */
const BRIDGED_TYPE_VALUE_KEYS = [
    'accessProfileBindingOperationTypes',
    'accessProfileStatuses',
    'accountStatuses',
    'assignmentStatuses',
    'connectorTypes',
    'dataClassificationTypes',
    'domainEntityTypes',
    'enforcementTypes',
    'environmentTypes',
    'functionGroupTypes',
    'identifierTypes',
    'identityTypes',
    'lifecycleStates',
    'partyRelationshipTypes',
    'partyTypes',
    'resourceResponsibilityTypes',
    'resourceTypes',
    'riskLevelTypes',
    'systemTypes',
    'workflowSteps',
] as const

function labelForLocale(
    tv: TypeValue,
    locale: string,
    resolved?: ReadonlyMap<string, string>,
): string {
    const resolvedLabel = resolved?.get(locale)
    if (resolvedLabel != null) return resolvedLabel
    return tv.translations?.[locale]?.label ?? tv.translations?.en?.label ?? tv.code
}

/**
 * Single entry in the post-processor index. Carries the row's full
 * translations map so the post-processor can resolve a `[[marker]]` against
 * the currently-active i18n locale without re-reading the bridge.
 */
export interface MarkerIndexEntry {
    code: string
    translations: Record<string, TranslationData>
}

/**
 * Atomically-swapped index from a lookup key (`code` or `alias`, lower-cased)
 * to the {@link DomainEntityType} row it resolves to. Issue 988.
 *
 * WHY: a single module-scope reference rather than per-call rebuild — the
 * bridge owns the only write, and the post-processor reads the current
 * snapshot on every render. Reference assignment is atomic in JS, so a
 * concurrent reader never sees a half-built map.
 */
let MARKER_INDEX: ReadonlyMap<string, MarkerIndexEntry> = new Map()

export function getEntityNounMarkerIndex(): ReadonlyMap<string, MarkerIndexEntry> {
    return MARKER_INDEX
}

function buildMarkerIndex(
    values: readonly TypeValue[],
    resolvedLabels: ReadonlyMap<string, ReadonlyMap<string, string>>,
): Map<string, MarkerIndexEntry> {
    const index = new Map<string, MarkerIndexEntry>()
    for (const tv of values) {
        if (!tv.active) continue
        const resolved = withResolvedTranslations(tv, resolvedLabels.get(tv.code))
        registerMarkerKey(index, tv.code, resolved)
        for (const alias of tv.aliases ?? []) {
            registerMarkerKey(index, alias, resolved)
        }
        // WHY (1005): register every locale's label as a synthetic alias so
        // [[Identity Management]] / [[identity management]] resolve via the
        // per-position case rule (label letters match) without requiring
        // hand-curated alias rows on every multi-word entity.
        for (const td of Object.values(resolved.translations ?? {})) {
            if (td?.label) registerMarkerKey(index, td.label, resolved)
        }
    }
    return index
}

function withResolvedTranslations(
    tv: TypeValue,
    resolved: ReadonlyMap<string, string> | undefined,
): TypeValue {
    if (!resolved || resolved.size === 0) return tv
    const next: Record<string, TranslationData> = {...tv.translations}
    for (const [locale, label] of resolved) {
        const existing = next[locale]
        if (existing) next[locale] = {...existing, label}
    }
    return {...tv, translations: next}
}

/**
 * Cross-reference resolver (issue 988.04). Walks every active row's labels,
 * substituting `[[code]]` markers with the referenced row's label
 * <strong>recursively</strong>, memoising per (code, locale) and detecting
 * cycles with a fail-fast throw.
 *
 * <p>Cross-references use the referenced row's label <em>verbatim</em> —
 * the per-position case rule from the runtime post-processor does NOT
 * apply here. Catalogue labels declare their own casing; substituting a
 * row's label is structural, not stylistic. (`[[FUNCTION_GROUP]] Assignment`
 * resolves to "Function Group Assignment", not "FUNCTION GROUP Assignment".)
 */
export function resolveLabelReferences(
    values: readonly TypeValue[],
): Map<string, ReadonlyMap<string, string>> {
    const lookup = new Map<string, TypeValue>()
    for (const tv of values) {
        if (!tv.active) continue
        const codeKey = tv.code.toLowerCase()
        if (!lookup.has(codeKey)) lookup.set(codeKey, tv)
        for (const alias of tv.aliases ?? []) {
            if (!alias) continue
            const aliasKey = alias.toLowerCase()
            if (!lookup.has(aliasKey)) lookup.set(aliasKey, tv)
        }
    }
    const resolved = new Map<string, Map<string, string>>()
    const visiting = new Set<string>()

    function resolve(tv: TypeValue, locale: string): string {
        const codeCache = resolved.get(tv.code)
        const cached = codeCache?.get(locale)
        if (cached != null) return cached
        const cycleKey = `${tv.code}|${locale}`
        if (visiting.has(cycleKey)) {
            throw new Error(
                // eslint-disable-next-line i18next/no-literal-string -- developer-facing boot-time error
                `Cyclic catalogue label reference involving '${tv.code}' (locale=${locale}). ` +
                // eslint-disable-next-line i18next/no-literal-string
                `Break the cycle by inlining one of the labels.`,
            )
        }
        visiting.add(cycleKey)
        const rawLabel = tv.translations?.[locale]?.label
            ?? tv.translations?.en?.label
            ?? tv.code
        const out = rawLabel.replace(createMarkerRegex(), (match, content: string) => {
            const target = lookup.get(content.toLowerCase())
            if (!target) {
                if (import.meta.env?.DEV) {
                    console.warn(
                        `[entity-noun-bridge] cross-reference [[${content}]] in label for ${tv.code} (${locale}) does not resolve`,
                    )
                }
                return match
            }
            return resolve(target, locale)
        })
        visiting.delete(cycleKey)
        const bucket = resolved.get(tv.code) ?? new Map<string, string>()
        bucket.set(locale, out)
        resolved.set(tv.code, bucket)
        return out
    }

    for (const tv of values) {
        if (!tv.active) continue
        for (const locale of Object.keys(tv.translations ?? {})) {
            resolve(tv, locale)
        }
    }
    return resolved
}

function registerMarkerKey(
    index: Map<string, MarkerIndexEntry>,
    rawKey: string,
    tv: TypeValue,
): void {
    if (!rawKey || rawKey.length === 0) return
    const lcKey = rawKey.toLowerCase()
    const existing = index.get(lcKey)
    if (existing && existing.code !== tv.code) {
        throw new Error(
            // eslint-disable-next-line i18next/no-literal-string -- developer-facing boot-time error
            `Ambiguous entity-noun marker key '${rawKey}' — resolves to both '${existing.code}' and '${tv.code}' (domain_entity_types row aliases collide). Rename the alias on one of the rows.`,
        )
    }
    index.set(lcKey, {code: tv.code, translations: tv.translations})
}

/**
 * Register every AppConfig type-table's labels as i18next resource bundle
 * entries under `appConfig.<typeTableName>.<CODE>`. After this fires, static
 * translations can use nesting: `"Create $t(appConfig.domainEntityTypes.IDENTITY)"`.
 *
 * <p>Reads raw type-value data (with the full per-locale translations map
 * intact), not localized output from `useAppConfig()` — same shape works
 * either way because `localizeTypeValues` preserves `translations`, but the
 * raw form is what the bridge naturally receives from the React Query cache.
 *
 * <p>Re-running with updated labels overwrites the existing entries (admin
 * renames take effect on the next call). Bridge skips early if i18next is
 * not yet initialised.
 *
 * <p>Also rebuilds the {@code [[marker]]} post-processor index from every
 * {@code domain_entity_types} row's code + aliases (issue 988). The new
 * index is built into a fresh Map, then assigned to the module-scope
 * reference in a single statement — readers never observe a half-built map.
 */
const DEFAULT_LOCALES = ['en'] as const

export function bridgeAppConfigToI18next(config: AppConfig): void {
    if (!i18next.isInitialized) return
    const locales = (config.supportedLocales ?? DEFAULT_LOCALES).filter((l) => typeof l === 'string' && l.length > 0)
    const domainEntityTypes = (config.domainEntityTypes ?? []) as TypeValue[]
    const resolvedDomainLabels = resolveLabelReferences(domainEntityTypes)
    let changed = false
    for (const locale of locales) {
        const bridged: Record<string, Record<string, string>> = {}
        for (const key of BRIDGED_TYPE_VALUE_KEYS) {
            const values = config[key] as TypeValue[] | undefined
            if (!values || values.length === 0) continue
            const isDomainEntity = key === 'domainEntityTypes'
            const codes: Record<string, string> = {}
            for (const tv of values) {
                const resolved = isDomainEntity ? resolvedDomainLabels.get(tv.code) : undefined
                codes[tv.code] = labelForLocale(tv, locale, resolved)
            }
            bridged[key] = codes
        }
        i18next.addResourceBundle(locale, 'translation', {appConfig: bridged}, true, true)
        changed = true
    }
    MARKER_INDEX = buildMarkerIndex(domainEntityTypes, resolvedDomainLabels)
    // WHY: addResourceBundle mutates i18next's internal store but does not
    // notify react-i18next subscribers, so components that already rendered
    // with unresolved $t(appConfig.domainEntityTypes.*) nesting stay stale
    // until something else triggers a re-render. Emitting 'languageChanged'
    // with the current language forces react-i18next to refresh every
    // useTranslation hook without actually changing the language.
    // eslint-disable-next-line i18next/no-literal-string -- i18next API event name, not user-facing text
    if (changed) i18next.emit('languageChanged', i18next.language)
}
